<template>
  <div class="compromisso-form">
    <!-- Basic Information Box -->
    <box 
      type="primary" 
:title="t('Informações básicas')" 
      elem-id="boxInformacoesGeraisCompromisso">
      
      <dl class="dl-horizontal" v-if="isEditMode">
        <dd><b>{{ t('ID') }}:</b> {{ compromissoData.compromisso_id }}</dd>
        <dd><b>{{ t('Franquia') }}:</b> {{ compromissoData.franquia_nome }}</dd>
        <dd><b>{{ t('Status') }}:</b> {{ compromissoData.compromisso_status }}</dd>
        <dd><b>{{ t('Total') }}:</b> {{ n(compromissoData.compromisso_total, 'currency') }}</dd>
      </dl>
    </box>

    <!-- Logistics Box -->
    <box 
      type="primary" 
      :title="t('Logística')" 
      elem-id="boxLogisticaCompromisso"
      :validation="() => logisticaValidationErrors">
      
      <div class="form-row">
        <div class="form-field">
          <select-list
            :label="t('Operador')"
            :placeholder="t('Escolha o operador')"
            :itens-select="operadoresOptions"
            :validation="() => isOperadorValid"
            :required="true"
            elem-id="compromisso_operador_user_id"
            data-testid="compromisso-operador-select"
            v-model="compromissoData.compromisso_operador_user_id">
          </select-list>
        </div>
        <div class="form-field">
          <franchise-select
            :label="t('Franquia')"
            :placeholder="t('Escolha a franquia')"
            :validation="() => isFranquiaValid"
            :required="true"
            elem-id="compromisso_franquia_id"
            v-model="compromissoData.compromisso_franquia_id"
            :callback="onFranquiaChange">
          </franchise-select>
        </div>
      </div>

      <div class="form-row">
        <div class="form-field">
          <input-date-time
            :label="t('Início')"
            :validation="() => isDataInicioValid"
            :required="true"
            elem-id="data-inicio"
            data-testid="compromisso-data-inicio"
            v-model="compromissoData.compromisso_data_hora_inicio">
          </input-date-time>
        </div>
        <div class="form-field">
          <input-date-time
            :label="t('Fim')"
            :validation="() => isDataFimValid"
            :required="true"
            elem-id="compromisso_data_hora_fim"
            data-testid="compromisso-data-fim"
            v-model="compromissoData.compromisso_data_hora_fim">
          </input-date-time>
        </div>
      </div>
    </box>

    <!-- Payment Box -->
    <box 
      type="primary" 
      :title="t('Pagamento')" 
      elem-id="boxPagamentoCompromisso"
      :validation="() => pagamentoValidationErrors">
      
      <div class="form-row">
        <div class="form-field">
          <select-list
            :label="t('Forma de pagamento')"
            :placeholder="t('Escolha a forma de pagamento')"
            :itens-select="formasPagtoOptions"
            :validation="() => isFormaPagtoValid"
            :required="true"
            elem-id="compromisso_forma_pagamento"
            data-testid="compromisso-forma-pagamento-select"
            v-model="compromissoData.compromisso_forma_pagamento">
          </select-list>
        </div>
        <div class="form-field">
          <input-number
            :label="t('Parcelas')"
            :placeholder="t('1')"
            :validation="() => isParcelasValid"
            :required="true"
            elem-id="compromisso_numero_parcelas"
            data-testid="compromisso-parcelas"
            v-model="compromissoData.compromisso_numero_parcelas">
          </input-number>
        </div>
      </div>
    </box>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, reactive, watch } from 'vue';
import { tComParametros } from '@/utils/tComParametros';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useFiltro } from '../../composables/useFiltro';
import { useCompromisso } from '../../composables/useCompromisso';
import { PARCELAS_MAX_PADRAO, parcelasMaxDaForma, type FormaComParcelamento } from '../../composables/useParcelamento';
import { useDateFormatter } from '../../composables/useDateFormatter';
import { permissionsService } from '../../services/permissionsService';

type Id = number | string;

interface SelectOption {
  ID: Id;
  Name: string;
}

interface FormaPagamento extends FormaComParcelamento {
  forma_pagamento_id: Id;
  forma_pagamento_nome: string;
}

interface CompromissoData {
  compromisso_id: number;
  compromisso_orcamento_id: number;
  compromisso_operador_user_id: string;
  compromisso_franquia_id: Id;
  compromisso_data_hora_inicio: string;
  compromisso_data_hora_fim: string;
  compromisso_forma_pagamento: string;
  compromisso_numero_parcelas: number;
  compromisso_status: string;
  compromisso_total: number;
  franquia_nome: string;
  compromisso_checkin: string;
  compromisso_checkout: string;
  compromisso_data_cadastro: string;
}

interface Props {
  isEditMode?: boolean;
}

withDefaults(defineProps<Props>(), {
  isEditMode: false
});

const emit = defineEmits<{
  'save-success': [];
  'action-success': [];
}>();

// Get i18n composable
const { t, n } = useI18n();

// Chave nova com parâmetros: nunca mostrar "{n}" cru (utils/tComParametros).
const tp = (chave: string, parametros: Record<string, string | number>): string => tComParametros(t, chave, parametros);
// API → App boundary: ISO 8601 UTC → hora local do navegador (o que o InputDateTime lê)
const { apiDateTimeToLocalInput, fromDateToApiDateTime } = useDateFormatter();

// Get filtro composable
useFiltro();

// Get compromisso composable
const {
  fetchCompromisso,
  fetchFormasPagamento,
  updateCompromisso
} = useCompromisso();

// Reactive data
const isLoading = ref<boolean>(false);

const compromissoData = reactive<CompromissoData>({
  compromisso_id: 0,
  compromisso_orcamento_id: 0,
  compromisso_operador_user_id: '',
  compromisso_franquia_id: '',
  compromisso_data_hora_inicio: '',
  compromisso_data_hora_fim: '',
  compromisso_forma_pagamento: '',
  compromisso_numero_parcelas: 1,
  compromisso_status: 'Compromisso agendado',
  compromisso_total: 0,
  franquia_nome: '',
  compromisso_checkin: '',
  compromisso_checkout: '',
  compromisso_data_cadastro: ''
});

const formasPagtoOptions = ref<SelectOption[]>([]);
const operadoresOptions = ref<SelectOption[]>([]);
// Franquia the current operadoresOptions (and the selected operador) belong to.
// FranchiseSelect fires its callback for the PROGRAMMATIC set made by loadForEdit
// too; comparing against this tells a real user change from that initial set.
const operadoresFranquiaId = ref<Id>('');

// Computed properties
const isOperadorValid = computed<boolean>(() => {
  return !!compromissoData.compromisso_operador_user_id &&
         compromissoData.compromisso_operador_user_id !== '';
});

const isFranquiaValid = computed<boolean>(() => {
  return !!compromissoData.compromisso_franquia_id &&
         compromissoData.compromisso_franquia_id !== '';
});

const isDataInicioValid = computed<boolean>(() => {
  if (!compromissoData.compromisso_data_hora_inicio ||
      !compromissoData.compromisso_data_hora_fim) {
    return false;
  }

  const inicio = new Date(compromissoData.compromisso_data_hora_inicio);
  const fim = new Date(compromissoData.compromisso_data_hora_fim);

  return inicio < fim;
});

const isDataFimValid = computed<boolean>(() => {
  if (!compromissoData.compromisso_data_hora_inicio ||
      !compromissoData.compromisso_data_hora_fim) {
    return false;
  }

  const inicio = new Date(compromissoData.compromisso_data_hora_inicio);
  const fim = new Date(compromissoData.compromisso_data_hora_fim);

  return fim > inicio;
});

/**
 * Mudar só o Início leva o Fim junto, mantendo a duração (chamado DS 2026-0066). Sem isso, quem
 * remarcava um compromisso para outro dia ficava com o Fim no dia antigo, antes do Início, e o
 * botão de salvar travava. Compromisso sem duração (Fim = Início) ganha 1 hora. Quando o Fim
 * também mudou na mesma hora (carregar a edição, a pessoa mexer nos dois), não se mexe nele.
 */
const DURACAO_PADRAO_MS = 60 * 60 * 1000;
watch(
  () => [compromissoData.compromisso_data_hora_inicio, compromissoData.compromisso_data_hora_fim] as const,
  ([inicio, fim], [inicioAntes, fimAntes]) => {
    if (isLoading.value || !inicio || inicio === inicioAntes || fim !== fimAntes || !inicioAntes || !fimAntes) return;
    const novoInicio = new Date(inicio).getTime();
    const duracao = new Date(fimAntes).getTime() - new Date(inicioAntes).getTime();
    if (Number.isNaN(novoInicio) || Number.isNaN(duracao)) return;
    compromissoData.compromisso_data_hora_fim = fromDateToApiDateTime(new Date(novoInicio + (duracao > 0 ? duracao : DURACAO_PADRAO_MS)));
  },
);

const isFormaPagtoValid = computed<boolean>(() => {
  return !!compromissoData.compromisso_forma_pagamento &&
         compromissoData.compromisso_forma_pagamento !== '';
});

// O limite é o da forma escolhida (chamado 2026-0067) — o mesmo que o recebimento vai aceitar.
// Sem forma escolhida ainda, vale o padrão, para não recusar antes da hora.
const formasPagamentoCarregadas = ref<FormaPagamento[]>([]);
const maxParcelas = computed<number>(() => {
  const forma = formasPagamentoCarregadas.value.find(
    (fp) => String(fp.forma_pagamento_id) === String(compromissoData.compromisso_forma_pagamento)
  );
  return forma ? parcelasMaxDaForma(forma) : PARCELAS_MAX_PADRAO;
});

const isParcelasValid = computed<boolean>(() => {
  const parcelas = parseInt(String(compromissoData.compromisso_numero_parcelas));
  return parcelas > 0 && parcelas <= maxParcelas.value;
});

const logisticaValidationErrors = computed<string[]>(() => {
  const errors: string[] = [];
  if (!isOperadorValid.value) errors.push(t('Escolha o operador'));
  if (!isDataInicioValid.value) errors.push(t('Data de início inválida'));
  if (!isDataFimValid.value) errors.push(t('Data de fim inválida'));
  return errors;
});

const pagamentoValidationErrors = computed<string[]>(() => {
  const errors: string[] = [];
  if (!isFormaPagtoValid.value) errors.push(t('Escolha a forma de pagamento'));
  if (!isParcelasValid.value) {
    errors.push(maxParcelas.value > 1
      ? tp('Número de parcelas inválido (máximo {n} nesta forma de pagamento)', { n: maxParcelas.value })
      : t('Esta forma de pagamento não parcela: use 1'));
  }
  return errors;
});

const canSave = computed<boolean>(() => {
  return isOperadorValid.value &&
         isFranquiaValid.value &&
         isDataInicioValid.value &&
         isDataFimValid.value &&
         isFormaPagtoValid.value &&
         isParcelasValid.value &&
         !isLoading.value &&
         compromissoData.compromisso_status !== 'Cancelado';
});

// Methods
const loadFormasPagamento = async (): Promise<void> => {
  const franquiaId = compromissoData.compromisso_franquia_id;
  const formasPagamento = await fetchFormasPagamento(Number(franquiaId) || null) as FormaPagamento[];
  if (franquiaId !== compromissoData.compromisso_franquia_id) return; // a franquia mudou enquanto carregava
  formasPagamentoCarregadas.value = formasPagamento;
  formasPagtoOptions.value = [
    { ID: '', Name: t('Escolha a forma de pagamento') },
    ...formasPagamento.map((fp) => ({
      ID: fp.forma_pagamento_id,
      Name: fp.forma_pagamento_nome
    }))
  ];
  log('CompromissoForm', 'loadFormasPagamento', 'Loaded formasPagto', formasPagtoOptions.value.length);
};

// A forma vale por franquia (chamado 2026-0023): parcelas e prazo dependem de qual é.
watch(() => compromissoData.compromisso_franquia_id, loadFormasPagamento);

const loadOptions = async (): Promise<void> => {
  log('CompromissoForm', 'loadOptions', 'Begin');
  try {
    // Franchises are now loaded reactively from selectedFranchises

    await loadFormasPagamento();

    // Load operators if franquia is selected
    if (compromissoData.compromisso_franquia_id) {
      await loadOperadores();
    }

  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('Error loading options:', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar opções: ') + message);
  }
};

// Franchise loading functions removed - now using reactive selectedFranchises from useFiltro

const loadOperadores = async (): Promise<void> => {
  log('CompromissoForm', 'loadOperadores', 'Begin', compromissoData.compromisso_franquia_id);
  try {
    const franquiaId = compromissoData.compromisso_franquia_id;
    if (!franquiaId) return;
    // Recorded BEFORE the await: FranchiseSelect's modelValue watcher (which fires
    // onFranquiaChange) runs in the microtask the await yields to.
    operadoresFranquiaId.value = franquiaId;

    const operadores = await permissionsService.getOperadoresSelectArrayByFranquiaID(Number(franquiaId), String(compromissoData.compromisso_operador_user_id || '')) as SelectOption[];
    operadoresOptions.value = [
      { ID: '', Name: t('Escolha o operador') },
      ...operadores
    ];
    log('CompromissoForm', 'loadOperadores', 'Loaded operadores', operadoresOptions.value.length);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('Error loading operadores:', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar operadores: ') + message);
  }
};

const onFranquiaChange = async (franquiaId?: Id): Promise<void> => {
  log('CompromissoForm', 'onFranquiaChange', 'Franquia changed', franquiaId);
  // Same franquia the operadores were loaded for => this is the initial
  // programmatic set (edit load), not a user change: keep the saved operador.
  if (String(franquiaId ?? '') === String(operadoresFranquiaId.value ?? '')) return;
  // Clear operador when franquia changes
  compromissoData.compromisso_operador_user_id = '';
  await loadOperadores();
};

const loadForEdit = async (compromissoId: Id): Promise<void> => {
  log('CompromissoForm', 'loadForEdit', 'Begin', compromissoId);
  try {
    isLoading.value = true;

    // Always load fresh options when opening modal for edit
    await loadOptions();

    const result = await fetchCompromisso(Number(compromissoId));
    const data = (result.compromisso || {}) as Partial<CompromissoData>;

    Object.assign(compromissoData, {
      ...data,
      compromisso_id: Number(data.compromisso_id) || 0,
      compromisso_orcamento_id: Number(data.compromisso_orcamento_id) || 0,
      compromisso_operador_user_id: data.compromisso_operador_user_id || '',
      compromisso_franquia_id: data.compromisso_franquia_id || '',
      compromisso_data_hora_inicio: apiDateTimeToLocalInput(data.compromisso_data_hora_inicio),
      compromisso_data_hora_fim: apiDateTimeToLocalInput(data.compromisso_data_hora_fim),
      compromisso_forma_pagamento: data.compromisso_forma_pagamento || '',
      compromisso_numero_parcelas: parseInt(String(data.compromisso_numero_parcelas)) || 1,
      compromisso_status: data.compromisso_status || 'Compromisso agendado',
      compromisso_total: parseFloat(String(data.compromisso_total)) || 0,
      franquia_nome: data.franquia_nome || '',
      compromisso_checkin: data.compromisso_checkin || '',
      compromisso_checkout: data.compromisso_checkout || '',
      compromisso_data_cadastro: data.compromisso_data_cadastro || ''
    });

    // Load operators for the selected franquia
    await loadOperadores();

  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('Error loading compromisso:', error);
    eventBus.emit('globalMessage.error', message);
  } finally {
    isLoading.value = false;
    log('CompromissoForm', 'loadForEdit', 'Finished');
  }
};

const saveCompromisso = async (): Promise<void> => {
  log('CompromissoForm', 'saveCompromisso', 'Attempt save');
  if (!canSave.value) return;

  try {
    isLoading.value = true;

    // The payment box and the franquia persist (decision 1,
    // docs/plans/doutor-flow-test-coverage.md §0.1); the contract types them as
    // numbers, the inputs may hold '' / a numeric string.
    await updateCompromisso({
      ...compromissoData,
      compromisso_forma_pagamento: Number(compromissoData.compromisso_forma_pagamento) || undefined,
      compromisso_numero_parcelas: Number(compromissoData.compromisso_numero_parcelas) || undefined,
      compromisso_franquia_id: Number(compromissoData.compromisso_franquia_id) || undefined,
    });

    log('CompromissoForm', 'saveCompromisso', 'Saved successfully');
    eventBus.emit('globalMessage.success', t('Compromisso salvo com sucesso!'));
    emit('save-success');

    // Trigger refresh event
    eventBus.emit('compromisso.saved');

  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('Error saving compromisso:', error);
    eventBus.emit('globalMessage.error', message);
  } finally {
    isLoading.value = false;
    log('CompromissoForm', 'saveCompromisso', 'Finished');
  }
};

const resetForm = (): void => {
  log('CompromissoForm', 'resetForm', 'Resetting form');
  Object.assign(compromissoData, {
    compromisso_id: 0,
    compromisso_orcamento_id: 0,
    compromisso_operador_user_id: '',
    compromisso_franquia_id: '',
    compromisso_data_hora_inicio: '',
    compromisso_data_hora_fim: '',
    compromisso_forma_pagamento: '',
    compromisso_numero_parcelas: 1,
    compromisso_status: 'Compromisso agendado',
    compromisso_total: 0,
    franquia_nome: '',
    compromisso_checkin: '',
    compromisso_checkout: '',
    compromisso_data_cadastro: ''
  });
  operadoresFranquiaId.value = '';
};

// Expose compromisso data for modal component
const getCompromissoData = (): CompromissoData => {
  log('CompromissoForm', 'getCompromissoData', 'Expose data');
  return compromissoData;
};

// Expose methods for parent components
defineExpose({
  loadForEdit,
  saveCompromisso,
  resetForm,
  getCompromissoData,
  canSave
});
</script>

<style scoped>
.form-group {
  margin-bottom: 15px;
}

.form-row {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 20px;
}

.form-field {
  flex: 1 1 calc(50% - 8px);
  min-width: 250px;
}

@media (max-width: 768px) {
  .form-field {
    flex: 1 1 100%;
  }
}

.dl-horizontal dd {
  margin-left: 0;
  margin-bottom: 5px;
}

.text-right {
  text-align: right;
}
</style>